import { Box, Image, Text } from '@chakra-ui/react';
import StructuredTextParser from '@moms/Utility/StructuredTextParser';
import WhatSetsMomApartSt from '@moms/styleData/WhatSetsMomApartSt';
import { render } from 'datocms-structured-text-to-html-string';
import React, { useEffect, useRef, useState } from 'react';

const WhatSetsMomApart = ({ sectionContent, region, lang }) => {
  const establishPractice = sectionContent.find(
    el => el.fieldId === 'establish-practice'
  );
  const cardList = sectionContent[3]?.cards || [];
  const sliderSectionRef = useRef(null);
  const triggerRefs = useRef([]);
  const animatingRef = useRef(false); // To block fast transitions
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    // Set up an IntersectionObserver on invisible triggers.
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Only trigger when 50% of the trigger is visible
    };

    const callback = entries => {
      // If we're in a locked (animating) state, ignore new entries.
      if (animatingRef.current) return;

      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          if (index !== activeIndex) {
            // Lock transitions for 1 second (adjust as needed)
            animatingRef.current = true;
            setActiveIndex(index);
            setTimeout(() => {
              animatingRef.current = false;
            }, 1000); // Matches animation duration
          }
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    triggerRefs.current.forEach(trigger => {
      if (trigger) observer.observe(trigger);
    });

    return () => {
      observer.disconnect();
    };
  }, [cardList.length, activeIndex]);

  return (
    <Box
      w="100%"
      bg="#000"
      py={{ base: '20px', md: '90px' }}
      {...WhatSetsMomApartSt[lang].container}
      className="text-container"
      display="flex"
      alignItems="center"
      pos="relative"
      _after={{
        content: "''",
        pos: 'absolute',
        height: '0.6px',
        left: '0',
        right: '0',
        width: '100%',
        bottom: '0',
        zIndex: '1',
        background:
          'radial-gradient(80.14% 21.43% at 50% 50%, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%), #010102',
        backdropFilter: 'blur(calc(var(--sds-size-blur-100) / 2))',
        borderTop: '1px solid rgba(255, 255, 255, 0.20)',
      }}
    >
      <Box
        {...WhatSetsMomApartSt[lang].containerChild}
        w="100%"
        maxW={930}
        mx="auto"
        px="1rem"
        position="relative"
      >
        {/* Outer container tall enough for all slides */}
        <Box
          ref={sliderSectionRef}
          position="relative"
          height={`${cardList.length * 100}vh`}
        >
          {/* Sticky slider container */}
          <Box position="sticky" top={0} height="100vh" overflow="hidden">
            {cardList.map((slide, index) => (
              <Box
                key={slide.id}
                className={`slide ${activeIndex === index ? 'active' : ''}`}
                position="absolute"
                top={{ base: 0, md: '100px', lg: 0 }}
                left={0}
                width="100%"
                height="100%"
                bg={slide.bg}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDir={{ base: 'column', lg: 'row' }}
                transition="opacity 0.3s ease"
                opacity={activeIndex === index ? 1 : 0}
              >
                {/* Mobile header text */}
                <Box
                  {...WhatSetsMomApartSt[lang].heading}
                  borderLeft={{ base: '5px solid rgba(236, 28, 45, 1)' }}
                  pl={{ base: '10px', md: '20px' }}
                  display={{ base: 'block', lg: 'none' }}
                  maxW="485px"
                >
                  {sectionContent[0]?.titleText}{' '}
                  <Box
                    as="span"
                    mr="8px"
                    {...WhatSetsMomApartSt[lang].subHeading}
                  >
                    {sectionContent[0]?.titleSubtext}
                  </Box>
                  {sectionContent[0]?.additionalTextLine}
                </Box>
                {/* Slide image */}
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  w={{ base: '100%', md: 'auto' }}
                  h={{ base: '95%', md: '100%' }}
                  className="slide_img"
                >
                  <Image
                    src={slide?.thumbnail?.url}
                    alt={slide.title}
                    w="auto"
                    maxW={{ base: '189px', md: '316px' }}
                    h={{ base: '372px', md: '90vh', lg: 'auto' }}
                    maxH={{ base: '40vh', lg: '80vh' }}
                    className="mobile-inner"
                  />
                </Box>
                {/* Slide content */}
                <Box
                  maxW="485px"
                  display="flex"
                  alignItems={{ base: 'center', lg: 'space-between' }}
                  flexDir="column"
                  justifyContent={{ base: 'center', lg: 'space-between' }}
                  h={{ base: '50vh', md: '100vh', lg: '500px' }}
                >
                  {/* Desktop header text */}
                  <Box
                    {...WhatSetsMomApartSt[lang].heading}
                    borderLeft={{ base: '5px solid rgba(236, 28, 45, 1)' }}
                    pl="20px"
                    display={{ base: 'none', lg: 'block' }}
                  >
                    {sectionContent[0]?.titleText}{' '}
                    <Box
                      as="span"
                      mr="8px"
                      {...WhatSetsMomApartSt[lang].subHeading}
                    >
                      {sectionContent[0]?.titleSubtext}
                    </Box>
                    {sectionContent[0]?.additionalTextLine}
                  </Box>
                  <Box
                    pt={{ base: '10px', lg: '50px' }}
                    pr={{ base: '15px', lg: '20px' }}
                    className="inner"
                  >
                    <Text {...WhatSetsMomApartSt[lang].title}>
                      {slide.title}
                    </Text>
                    <StructuredTextParser
                      str={render(slide?.description)}
                      mt="5px"
                      maxW="100%"
                      {...WhatSetsMomApartSt?.[lang]?.mainHeading}
                    />
                    <StructuredTextParser
                      str={render(slide.moreDetailsText)}
                      mt="5px"
                      maxW="100%"
                      {...WhatSetsMomApartSt[lang].description}
                    />
                  </Box>
                  {/* Dot Indicator */}
                  <Box
                    position="absolute"
                    top={{ base: '70%', md: '60%', lg: '58%' }}
                    right="0px"
                    display="flex"
                    flexDirection="column"
                    gap="8px"
                  >
                    {cardList.map((_, dotIndex) => (
                      <Box
                        key={dotIndex}
                        width="2px"
                        height="24px"
                        border="1px solid"
                        borderColor={
                          dotIndex === activeIndex
                            ? 'rgba(240, 30, 31, 1)'
                            : 'rgba(255, 255, 255, 0.2)'
                        }
                        bg={dotIndex === activeIndex ? 'red' : 'transparent'}
                        cursor="pointer"
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
          {/* Invisible triggers to detect scroll position */}
          {cardList.map((_, index) => (
            <Box
              key={`trigger-${index}`}
              data-index={index}
              ref={el => (triggerRefs.current[index] = el)}
              height="100vh"
              position="absolute"
              top={`${index * 100}vh`}
              width="100%"
              opacity={0}
              pointerEvents="none"
            />
          ))}
        </Box>
        {/* Bottom description (fixed below slider) */}
        <StructuredTextParser
          str={render(establishPractice.body)}
          my="15px"
          maxW={{ base: '100%', md: '75%', xl: '100%' }}
          {...WhatSetsMomApartSt?.[lang]?.bottomDesc}
        />
      </Box>
      <style jsx global>{`
        /* Animation for desktop text */
        .active .inner {
          animation: fadeInUp 0.8s forwards;
        }
        /* Animation for mobile image */
        .active .mobile-inner {
          animation: mobileFadeInUp 0.8s forwards;
        }
        @keyframes mobileFadeInUp {
          from {
            opacity: 1;
            transform: translateY(260px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInUp {
          0% {
            opacity: 1;
            transform: translateY(60px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default WhatSetsMomApart;
